/**
 * ReaderSettings - 閱讀器設定面板
 */

import { useState } from 'react';
import { getThemeStyles, FONT_FAMILY_MAP, TAP_LAYOUT_CONFIG, type FontFamily } from '../../hooks/useReaderSettings';
import type { ReaderSettingsProps } from './types';

export function ReaderSettings({
  fontSize,
  lineHeight,
  paragraphSpacing,
  theme,
  fontFamily,
  tapNavigationEnabled,
  tapLayout,
  swipeNavigationEnabled,
  setFontSize,
  setLineHeight,
  setParagraphSpacing,
  setTheme,
  setFontFamily,
  setTapNavigationEnabled,
  setTapLayout,
  setSwipeNavigationEnabled,
  isTouchDevice,
  isDesktop,
  themeStyles,
}: ReaderSettingsProps) {
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const isDark = theme === 'dark';

  return (
    <div
      className="border-t px-4 py-4"
      style={{
        backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
        borderColor: theme === 'dark' ? '#444' : '#e5e7eb',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="max-w-3xl mx-auto space-y-4">
        {/* 字體大小 */}
        <div className="flex items-center gap-4">
          <span className="text-sm w-16" style={{ color: themeStyles.color }}>
            字體大小
          </span>
          <input
            type="range"
            min="14"
            max="28"
            value={fontSize}
            onChange={(e) => setFontSize(Number(e.target.value))}
            className="flex-1 accent-primary-600"
          />
          <span
            className="text-sm w-12 text-right"
            style={{ color: themeStyles.color, opacity: 0.6 }}
          >
            {fontSize}px
          </span>
        </div>

        {/* 字體選擇 */}
        <div className="flex items-center gap-4">
          <span className="text-sm w-16" style={{ color: themeStyles.color }}>
            字體
          </span>
          <div className="flex gap-2 flex-1">
            {([
              { key: 'system' as FontFamily, label: '系統' },
              { key: 'serif' as FontFamily, label: '襯線' },
              { key: 'sans-serif' as FontFamily, label: '無襯線' },
            ]).map((font) => (
              <button
                key={font.key}
                onClick={() => setFontFamily(font.key)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  fontFamily === font.key
                    ? 'bg-primary-600 text-white'
                    : theme === 'dark'
                    ? 'bg-gray-700 hover:bg-gray-600'
                    : 'bg-gray-200 hover:bg-gray-300'
                }`}
                style={{
                  fontFamily: FONT_FAMILY_MAP[font.key],
                  color: fontFamily === font.key ? 'white' : themeStyles.color,
                }}
              >
                {font.label}
              </button>
            ))}
          </div>
        </div>

        {/* 行高 */}
        <div className="flex items-center gap-4">
          <span className="text-sm w-16" style={{ color: themeStyles.color }}>
            行高
          </span>
          <input
            type="range"
            min="1.4"
            max="2.4"
            step="0.1"
            value={lineHeight}
            onChange={(e) => setLineHeight(Number(e.target.value))}
            className="flex-1 accent-primary-600"
          />
          <span
            className="text-sm w-12 text-right"
            style={{ color: themeStyles.color, opacity: 0.6 }}
          >
            {lineHeight}
          </span>
        </div>

        {/* 段落間距 */}
        <div className="flex items-center gap-4">
          <span className="text-sm w-16" style={{ color: themeStyles.color }}>
            段落間距
          </span>
          <input
            type="range"
            min="0.5"
            max="2.0"
            step="0.25"
            value={paragraphSpacing}
            onChange={(e) => setParagraphSpacing(Number(e.target.value))}
            className="flex-1 accent-primary-600"
          />
          <span
            className="text-sm w-12 text-right"
            style={{ color: themeStyles.color, opacity: 0.6 }}
          >
            {paragraphSpacing}em
          </span>
        </div>

        {/* 主題 */}
        <div className="flex items-center gap-4">
          <span className="text-sm w-16" style={{ color: themeStyles.color }}>
            主題
          </span>
          <div className="flex gap-2">
            {(['light', 'sepia', 'dark'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTheme(t)}
                className={`w-8 h-8 rounded-full border-2 transition-all ${
                  theme === t ? 'border-primary-500 scale-110' : 'border-gray-300'
                }`}
                style={{ backgroundColor: getThemeStyles(t).background }}
              />
            ))}
          </div>
        </div>

        {/* 點擊快跳 */}
        <div className="flex items-center gap-4">
          <span className="text-sm w-16" style={{ color: themeStyles.color }}>
            點擊快跳
          </span>
          <button
            onClick={() => setTapNavigationEnabled(!tapNavigationEnabled)}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              tapNavigationEnabled ? 'bg-primary-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                tapNavigationEnabled ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* 點擊區域佈局 */}
        {tapNavigationEnabled && (
          <div className="flex items-center gap-4">
            <span className="text-sm w-16" style={{ color: themeStyles.color }}>
              點擊佈局
            </span>
            <div className="flex gap-2 flex-1">
              {(['leftRight-B', 'leftRight-A', 'topBottom'] as const).map((layout) => (
                <button
                  key={layout}
                  onClick={() => setTapLayout(layout)}
                  className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${
                    tapLayout === layout
                      ? 'bg-primary-600 text-white'
                      : isDark
                      ? 'bg-neutral-700 hover:bg-neutral-600'
                      : 'bg-neutral-200 hover:bg-neutral-300'
                  }`}
                  style={{
                    color: tapLayout === layout ? 'white' : themeStyles.color,
                  }}
                  aria-label={TAP_LAYOUT_CONFIG[layout].description}
                >
                  {TAP_LAYOUT_CONFIG[layout].label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 滑動翻頁 (觸控裝置) */}
        {isTouchDevice && (
          <div className="flex items-center gap-4">
            <span className="text-sm w-16" style={{ color: themeStyles.color }}>
              滑動翻頁
            </span>
            <button
              onClick={() => setSwipeNavigationEnabled(!swipeNavigationEnabled)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                swipeNavigationEnabled ? 'bg-primary-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  swipeNavigationEnabled ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        )}

        {/* 鍵盤快捷鍵說明 (桌面版) */}
        {isDesktop && (
          <div
            className="border-t pt-4 mt-2"
            style={{ borderColor: theme === 'dark' ? '#444' : '#e5e7eb' }}
          >
            <button
              onClick={() => setShowKeyboardHelp(!showKeyboardHelp)}
              className="flex items-center gap-2 text-sm w-full"
              style={{ color: themeStyles.color, opacity: 0.7 }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                />
              </svg>
              <span>鍵盤快捷鍵</span>
              <svg
                className={`w-4 h-4 ml-auto transition-transform ${
                  showKeyboardHelp ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showKeyboardHelp && (
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                {[
                  { key: 'Space', label: '下一頁' },
                  { key: 'PageUp', label: '上一頁' },
                  { key: '←', label: '上一章' },
                  { key: '→', label: '下一章' },
                  { key: 'F', label: '全螢幕閱讀' },
                  { key: 'Esc', label: '顯示介面' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2">
                    <kbd
                      className="px-2 py-1 rounded text-xs font-mono"
                      style={{
                        backgroundColor: theme === 'dark' ? '#444' : '#f3f4f6',
                        color: themeStyles.color,
                      }}
                    >
                      {key}
                    </kbd>
                    <span style={{ color: themeStyles.color, opacity: 0.7 }}>{label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
